import { useMemo, useState } from 'react';
import type { ProcessSummary } from '@pm2-monitor/shared';
import { useStore } from '../../state/store';
import { useWindowedSeries } from '../../state/useSeries';
import { axisLabels } from '../../lib/chart';
import {
  RANGE_LABELS,
  restartTone,
  statusColors,
  statusPill,
  timeAxis,
} from '../../lib/derive';
import {
  formatAgo,
  formatClock,
  formatCpu,
  formatGiB,
  formatMemory,
  formatPercent,
  formatUptime,
  pluralize,
  toMiB,
} from '../../lib/format';
import { SeriesChart, Sparkline, XAxis, YAxis } from '../charts';
import {
  DefRow,
  Dot,
  Heading,
  Numeral,
  Panel,
  Pill,
  Placeholder,
  Sub,
  panelStyle,
  tabularNums,
} from '../ui';
import { LogRow } from './LogRow';

export function Overview() {
  const processes = useStore((s) => s.processes);
  const system = useStore((s) => s.system);
  const alerts = useStore((s) => s.alerts);
  const rules = useStore((s) => s.rules);
  const logs = useStore((s) => s.logs);
  const processCpu = useStore((s) => s.processCpu);
  const selectedId = useStore((s) => s.selectedId);
  const select = useStore((s) => s.select);
  const setView = useStore((s) => s.setView);
  const now = useStore((s) => s.now);
  const range = useStore((s) => s.range);

  const series = useWindowedSeries();

  const online = processes.filter((p) => p.status === 'online');
  const totalCpu = online.reduce((n, p) => n + p.cpu, 0);
  const totalMemory = online.reduce((n, p) => n + p.memory, 0);
  const byMemory = useMemo(
    () => [...processes].sort((a, b) => b.memory - a.memory),
    [processes],
  );
  const peakMemory = Math.max(...online.map((p) => p.memory), 1);
  const topProcess = byMemory.find((p) => p.status === 'online');

  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;
  const oldestAlert = alerts.reduce<number | null>(
    (oldest, a) => (oldest === null || a.triggeredAt < oldest ? a.triggeredAt : oldest),
    null,
  );
  const armedRules = rules.filter((r) => r.enabled).length;

  const down = processes.filter((p) => p.status !== 'online');
  const withoutAutorestart = processes.filter((p) => !p.autorestart).length;
  const downCopy =
    down.length === 0
      ? 'every process running'
      : `${down
          .slice(0, 2)
          .map((p) => `${p.name} ${p.status}`)
          .join(' · ')}${down.length > 2 ? ` · +${down.length - 2} more` : ''}`;
  const autorestartCopy =
    withoutAutorestart === 0
      ? 'all autorestart'
      : `${withoutAutorestart} without autorestart`;

  const peakCpu = series.cpu.length > 0 ? Math.max(...series.cpu) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ── key figures ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))',
          gap: 16,
        }}
      >
        <Kpi
          label="Processes online"
          chip={
            <Pill
              bg="var(--pos-tint)"
              fg="var(--pos)"
              strong
            >{`${online.length} up`}</Pill>
          }
          value={String(online.length)}
          unit={`/${processes.length}`}
          footnote={`${downCopy} · ${autorestartCopy}`}
        />
        <Kpi
          label="Total CPU"
          chip={<Pill strong>{pluralize(system?.cpuCount ?? 0, 'core')}</Pill>}
          value={totalCpu.toFixed(1)}
          unit="%"
          footnote={`Peak ${peakCpu.toFixed(0)}% in the last ${
            series.partial ? `${series.coveredSeconds}s` : RANGE_LABELS[range]
          }`}
        />
        <Kpi
          label="Resident memory"
          chip={
            <Pill strong>{system ? `of ${formatGiB(system.totalMemory)}` : '—'}</Pill>
          }
          value={toMiB(totalMemory).toFixed(0)}
          unit="MiB"
          footnote={
            topProcess
              ? `${topProcess.name} holds ${formatPercent(topProcess.memory, totalMemory)}% of it`
              : 'nothing resident'
          }
        />
        <Kpi
          label="Active alerts"
          chip={
            criticalCount > 0 ? (
              <Pill
                bg="var(--neg-tint)"
                fg="var(--neg)"
                strong
              >{`${criticalCount} critical`}</Pill>
            ) : (
              <Pill strong>none critical</Pill>
            )
          }
          value={String(alerts.length)}
          color={alerts.length > 0 ? 'var(--neg)' : undefined}
          footnote={`${
            oldestAlert === null
              ? 'nothing firing'
              : `Oldest ${formatAgo(oldestAlert, now)}`
          } · ${pluralize(armedRules, 'rule')} armed`}
        />
      </div>

      {/* ── load + memory split ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))',
          gap: 16,
        }}
      >
        <ServerLoad />

        <Panel>
          <Heading>Memory by process</Heading>
          <Sub>Resident set, largest first</Sub>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              marginTop: 20,
            }}
          >
            {online.length === 0 ? (
              <Placeholder
                title="Nothing online"
                detail="No process is currently holding memory."
              />
            ) : (
              [...online]
                .sort((a, b) => b.memory - a.memory)
                .map((p) => (
                  <MemoryBar
                    key={p.id}
                    name={p.name}
                    value={formatMemory(p.memory)}
                    width={(p.memory / peakMemory) * 100}
                    fill={p.memory === peakMemory ? 'var(--pos)' : 'var(--ink)'}
                  />
                ))
            )}
          </div>
        </Panel>
      </div>

      {/* ── process table ── */}
      <div style={{ ...panelStyle, padding: '22px 24px 8px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            marginBottom: 6,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <Heading>Processes</Heading>
            <Sub>
              {pluralize(processes.length, 'process', 'processes')} managed by pm2{' '}
              {system?.pm2Version ?? ''} — select a row for detail
            </Sub>
          </div>
          <div
            style={{
              marginLeft: 'auto',
              fontSize: 12.5,
              color: 'var(--faint)',
            }}
          >
            Sorted by memory
          </div>
        </div>

        <div className="scroll-x">
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 13.5,
              minWidth: 760,
            }}
          >
            <thead>
              <tr>
                {[
                  { label: 'Process', align: 'left' as const },
                  { label: 'Status', align: 'left' as const, width: 96 },
                  { label: 'CPU', align: 'right' as const, width: 72 },
                  { label: 'Trend', align: 'left' as const, width: 104 },
                  { label: 'Memory', align: 'right' as const, width: 92 },
                  { label: 'Restarts', align: 'right' as const, width: 88 },
                  { label: 'Inst.', align: 'right' as const, width: 64 },
                  { label: 'Uptime', align: 'right' as const, width: 76 },
                  { label: 'PID', align: 'right' as const, width: 80 },
                ].map((col) => (
                  <th
                    key={col.label}
                    scope="col"
                    style={{
                      textAlign: col.align,
                      fontSize: 12,
                      fontWeight: 400,
                      color: 'var(--faint)',
                      padding: '12px 10px',
                      borderBottom: '1px solid var(--line)',
                      ...(col.width ? { width: col.width } : {}),
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {byMemory.map((p) => (
                <ProcessRow
                  key={p.id}
                  process={p}
                  cpuHistory={processCpu[p.id] ?? []}
                  cpuCeiling={series.cpuCeiling}
                  selected={selectedId === p.id}
                  now={now}
                  onOpen={() => select(p.id)}
                />
              ))}
              {processes.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <Placeholder
                      title="No processes"
                      detail="The pm2 daemon is reachable but has nothing under management."
                    />
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── recent output ── */}
      <Panel style={{ padding: '22px 24px 20px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            marginBottom: 12,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <Heading>Recent output</Heading>
            <Sub>Merged stdout and stderr across every process</Sub>
          </div>
          <button
            type="button"
            onClick={() => setView('logs')}
            style={{
              marginLeft: 'auto',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--pos)',
            }}
          >
            Open the full stream →
          </button>
        </div>
        {logs.length === 0 ? (
          <Placeholder
            title="No output yet"
            detail="Nothing has been written to stdout or stderr since the bridge started buffering."
          />
        ) : (
          [...logs]
            .slice(-8)
            .reverse()
            .map((line) => (
              <LogRow key={line.key} line={line} showProcess variant="compact" />
            ))
        )}
      </Panel>
    </div>
  );
}

function Kpi({
  label,
  chip,
  value,
  unit,
  footnote,
  color,
}: {
  label: string;
  chip: React.ReactNode;
  value: string;
  unit?: string;
  footnote: string;
  color?: string;
}) {
  return (
    <div style={{ ...panelStyle, padding: '20px 22px 18px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>{label}</div>
        {chip}
      </div>
      <div style={{ marginTop: 16 }}>
        <Numeral value={value} unit={unit} color={color} />
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--faint)', marginTop: 10 }}>
        {footnote}
      </div>
    </div>
  );
}

export function MemoryBar({
  name,
  value,
  width,
  fill,
}: {
  name: string;
  value: string;
  width: number;
  fill: string;
}) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 7,
        }}
      >
        <div
          style={{
            fontSize: 13,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            flex: 'none',
            ...tabularNums,
          }}
        >
          {value}
        </div>
      </div>
      <div
        style={{
          height: 7,
          borderRadius: 999,
          background: 'var(--page)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            borderRadius: 999,
            background: fill,
            width: `${Math.max(0, Math.min(100, width)).toFixed(1)}%`,
          }}
        />
      </div>
    </div>
  );
}

function ProcessRow({
  process,
  cpuHistory,
  cpuCeiling,
  selected,
  now,
  onOpen,
}: {
  process: ProcessSummary;
  cpuHistory: readonly number[];
  cpuCeiling: number;
  selected: boolean;
  now: number;
  onOpen: () => void;
}) {
  const colors = statusColors(process.status);
  const pill = statusPill(process.status);
  const restarts = restartTone(process);
  const cell = {
    padding: '13px 10px',
    borderBottom: '1px solid var(--line-soft)',
  } satisfies React.CSSProperties;

  return (
    <tr
      className="hoverable"
      onClick={onOpen}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{
        cursor: 'pointer',
        background: selected ? 'var(--hover)' : 'transparent',
      }}
    >
      <td style={cell}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Dot color={colors.dot} ring={colors.ring} />
          <div style={{ fontWeight: 600 }}>{process.name}</div>
        </div>
      </td>
      <td style={cell}>
        <Pill bg={pill.bg} fg={pill.fg}>
          {process.status}
        </Pill>
      </td>
      <td style={{ ...cell, textAlign: 'right', ...tabularNums }}>
        {formatCpu(process.cpu)}
      </td>
      <td style={cell}>
        <Sparkline values={cpuHistory.slice(-30)} ceiling={cpuCeiling} />
      </td>
      <td style={{ ...cell, textAlign: 'right', fontWeight: 600, ...tabularNums }}>
        {formatMemory(process.memory)}
      </td>
      <td style={{ ...cell, textAlign: 'right' }}>
        <Pill bg={restarts.bg} fg={restarts.fg} style={{ ...tabularNums }}>
          {process.restarts}
        </Pill>
      </td>
      <td
        style={{
          ...cell,
          textAlign: 'right',
          color: 'var(--muted)',
          ...tabularNums,
        }}
      >
        ×{process.instances}
      </td>
      <td
        style={{
          ...cell,
          textAlign: 'right',
          color: 'var(--muted)',
          ...tabularNums,
        }}
      >
        {formatUptime(process.status === 'online' ? process.uptimeSince : null, now)}
      </td>
      <td
        style={{
          ...cell,
          textAlign: 'right',
          color: 'var(--faint)',
          ...tabularNums,
        }}
      >
        {process.pid ?? '—'}
      </td>
    </tr>
  );
}

/**
 * The headline chart. CPU is read against the left axis; memory shares the plot
 * as a shape only, so its ceiling is stated in the legend rather than implied.
 */
function ServerLoad() {
  const series = useWindowedSeries();
  const statusIntervalMs = useStore((s) => s.statusIntervalMs);
  const processes = useStore((s) => s.processes);
  const [hover, setHover] = useState<number | null>(null);

  const points = series.plotCpu.length;
  const index = hover === null ? points - 1 : Math.min(hover, points - 1);
  const cpuAt = series.plotCpu[index] ?? 0;
  const memAt = series.plotMem[index] ?? 0;
  const timeAt = series.plotTimes[index];

  return (
    <Panel>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 20,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Heading>Server load</Heading>
          <Sub>
            Aggregate of {pluralize(processes.length, 'process', 'processes')}, one status
            frame every{' '}
            {(statusIntervalMs / 1000).toFixed(statusIntervalMs % 1000 === 0 ? 0 : 1)}s
          </Sub>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 16,
            flex: 'none',
            paddingTop: 3,
            flexWrap: 'wrap',
          }}
        >
          <Legend color="var(--pos)" label={`CPU % (0–${series.cpuCeiling})`} />
          <Legend color="var(--faint)" label={`Memory MiB (0–${series.memCeiling})`} />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 28,
          margin: '18px 0 6px',
          flexWrap: 'wrap',
        }}
      >
        <Readout
          label={`${hover === null ? 'Live' : 'Sampled'} CPU`}
          value={cpuAt.toFixed(1)}
          unit="%"
        />
        <Readout
          label={`${hover === null ? 'Live' : 'Sampled'} memory`}
          value={memAt.toFixed(0)}
          unit="MiB"
          muted
        />
        <div
          style={{
            marginLeft: 'auto',
            fontSize: 12.5,
            color: 'var(--faint)',
            ...tabularNums,
          }}
        >
          {timeAt === undefined ? '—' : formatClock(timeAt)}
        </div>
      </div>

      <div
        style={{ position: 'relative', display: 'flex', gap: 10 }}
        onMouseMove={(e) => {
          if (points < 2) return;
          const rect = e.currentTarget.getBoundingClientRect();
          const ratio = (e.clientX - rect.left - 44) / Math.max(1, rect.width - 44);
          const i = Math.max(0, Math.min(points - 1, Math.round(ratio * (points - 1))));
          if (i !== hover) setHover(i);
        }}
        onMouseLeave={() => setHover(null)}
      >
        <YAxis labels={axisLabels(series.cpuCeiling)} />
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <SeriesChart
              height={216}
              series={[
                {
                  values: series.plotCpu,
                  ceiling: series.cpuCeiling,
                  color: 'var(--pos)',
                  width: 2.5,
                  area: true,
                },
                {
                  values: series.plotMem,
                  ceiling: series.memCeiling,
                  color: 'var(--faint)',
                  width: 2,
                },
              ]}
            />
            {hover !== null && points > 1 ? (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${((index / (points - 1)) * 100).toFixed(2)}%`,
                  width: 1,
                  background: 'var(--muted)',
                  pointerEvents: 'none',
                }}
              />
            ) : null}
          </div>
          <XAxis labels={timeAxis(series.coveredSeconds)} />

          {hover !== null && timeAt !== undefined ? (
            <div
              style={{
                position: 'absolute',
                top: 8,
                left: `${((index / Math.max(1, points - 1)) * 100).toFixed(2)}%`,
                transform: 'translateX(-50%)',
                pointerEvents: 'none',
                background: 'var(--panel)',
                border: '1px solid var(--line)',
                borderRadius: 12,
                boxShadow: 'var(--card)',
                padding: '9px 13px',
                whiteSpace: 'nowrap',
              }}
            >
              <div
                style={{
                  fontSize: 11.5,
                  color: 'var(--faint)',
                  ...tabularNums,
                }}
              >
                {formatClock(timeAt)}
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  marginTop: 3,
                  ...tabularNums,
                }}
              >
                {cpuAt.toFixed(1)}% cpu · {memAt.toFixed(0)} MiB
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {series.partial ? (
        <div
          style={{
            fontSize: 12,
            color: 'var(--faint)',
            marginTop: 12,
            textWrap: 'pretty',
          }}
        >
          Showing {series.coveredSeconds}s. The bridge keeps a 60-second window and no
          history, so longer ranges fill in from the live stream while this tab stays
          open.
        </div>
      ) : null}
    </Panel>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <div style={{ width: 8, height: 8, borderRadius: 999, background: color }} />
      <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{label}</div>
    </div>
  );
}

function Readout({
  label,
  value,
  unit,
  muted,
}: {
  label: string;
  value: string;
  unit: string;
  muted?: boolean;
}) {
  return (
    <div>
      <div style={{ fontSize: 12.5, color: 'var(--faint)' }}>{label}</div>
      <div style={{ marginTop: 5 }}>
        <Numeral
          value={value}
          unit={unit}
          size={30}
          color={muted ? 'var(--muted)' : undefined}
        />
      </div>
    </div>
  );
}

export { DefRow };
